import { combineReducers, configureStore } from '@reduxjs/toolkit';

import {
  isPlayCountsState,
  playCountsReducer,
  type PlayCountsState,
} from '@/features/attempts/playCountsSlice';
import { flushWhenOnline, outboxListener } from '@/features/outbox/outboxMiddleware';
import {
  isOutboxState,
  outboxReducer,
  persistableOutbox,
  type OutboxState,
} from '@/features/outbox/outboxSlice';
import {
  initialSettings,
  isSettingsState,
  settingsReducer,
  type SettingsState,
} from '@/features/settings/settingsSlice';
import { baseApi } from '@/shared/api/baseApi';
import { rememberSession, type SessionKind } from '@/shared/api/sessionHint';
import { sessionReducer } from '@/shared/api/sessionSlice';
import { createPersistence, type Migration } from '@/shared/lib/persistence';

const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  session: sessionReducer,
  settings: settingsReducer,
  outbox: outboxReducer,
  playCounts: playCountsReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

/**
 * What survives a reload: device preferences, unsent work and the listens already
 * used in open attempts. Server data is refetched and the access token is
 * deliberately never stored.
 */
interface Persisted {
  settings: SettingsState;
  outbox: OutboxState;
  playCounts: PlayCountsState;
}

/** Upgrades of the stored shape, keyed by the version each one produces. */
export const PERSISTENCE_MIGRATIONS: Record<number, Migration> = {
  // 2: listens used per attempt item are kept, so that a reload does not refill them.
  2: (state) => ({ ...state, playCounts: { counts: {} } }),
};

const persistence = createPersistence<Persisted>({
  key: 'easymusic:state',
  // Bump when the shape of `Persisted` changes, and add a migration for old data.
  version: 2,
  migrations: PERSISTENCE_MIGRATIONS,
  validate: (state): state is Persisted =>
    typeof state === 'object' &&
    state !== null &&
    isSettingsState((state as Persisted).settings) &&
    isOutboxState((state as Persisted).outbox) &&
    isPlayCountsState((state as Persisted).playCounts),
});

export function createStore(preloaded: Partial<Persisted> | undefined = persistence.load()) {
  const store = configureStore({
    reducer: rootReducer,
    preloadedState: preloaded
      ? {
          settings: { ...initialSettings, ...preloaded.settings },
          outbox: preloaded.outbox ?? { items: [], failures: {} },
          playCounts: preloaded.playCounts ?? { counts: {} },
        }
      : undefined,
    middleware: (getDefault) =>
      getDefault().prepend(outboxListener.middleware).concat(baseApi.middleware),
  });
  return store;
}

export type AppStore = ReturnType<typeof createStore>;

/** Wire the store to the browser: persistence and network recovery. Returns a cleanup. */
export function connectStore(store: AppStore): () => void {
  const detach = persistence.attach(
    store,
    (state: RootState): Persisted => ({
      settings: state.settings,
      outbox: persistableOutbox(state.outbox),
      playCounts: state.playCounts,
    }),
  );
  const stopListening = flushWhenOnline(store.dispatch);
  // Note on this device whether a session exists, and whose, so the next start knows
  // whether to restore one (and the front's static pages, whom they greet).
  let status = store.getState().session.status;
  let kind = sessionKind(store.getState());
  const unsubscribe = store.subscribe(() => {
    const state = store.getState();
    const next = sessionKind(state);
    if (state.session.status === status && next === kind) return;
    status = state.session.status;
    kind = next;
    if (status !== 'unknown') rememberSession(next);
  });
  return () => {
    detach();
    stopListening();
    unsubscribe();
  };
}

function sessionKind(state: RootState): SessionKind {
  if (state.session.status !== 'authenticated') return null;
  return state.session.user?.role === 'guest' ? 'guest' : 'account';
}
